import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'

const useStyles = makeStyles(() => ({
  '@keyframes fillTransition': {
    from: { fill: 'var(--color-neutral-100)' },
    to: { fill: 'var(--color-neutral-200)' }
  },
  skeletonAnimation: {
    animation: '$fillTransition 1s infinite alternate',
    fill: 'var(--color-neutral-100)'
  }
}))

const AxisChartLoadingState = ({
  animate = true
}) => {
  const classes = useStyles()
  const loadingContainerRef = useRef(null)
  const [dimensions, setDimensions] = useState({ width: 800, height: 400 })

  useLayoutEffect(() => {
    if (loadingContainerRef.current) {
      const { clientWidth, clientHeight } = loadingContainerRef.current
      setDimensions({
        width: clientWidth || 800,
        height: clientHeight || 400
      })
    }
  }, [])

  const layout = useMemo(() => {
    const { width, height } = dimensions
    const strokeWidth = 16
    const edgeOffset = 10

    const yAxisX = edgeOffset * 4
    const xAxisY = height - (edgeOffset * 5) - (strokeWidth / 2)
    const yAxisTop = (edgeOffset * 3) + (strokeWidth / 2)
    const yAxisBottom = height - (edgeOffset * 5) - (strokeWidth / 2)
    const xAxisRight = width - (edgeOffset * 3)

    return {
      strokeWidth,
      edgeOffset,
      yAxisX,
      xAxisY,
      yAxisTop,
      yAxisBottom,
      xAxisRight,
      backgroundRect: {
        x: edgeOffset,
        y: edgeOffset,
        width: width - edgeOffset,
        height: height - (edgeOffset * 4),
        rx: 12
      }
    }
  }, [dimensions])

  const animations = useMemo(() => {
    const { width, height } = dimensions
    const { strokeWidth, edgeOffset, yAxisX } = layout

    const pathStartX = yAxisX + (strokeWidth / 2)
    const pathStartY = edgeOffset * 3
    const pathMidY = height - (edgeOffset * 5) - strokeWidth
    const pathEndX = width - (edgeOffset * 6) - (strokeWidth * 3)

    const tracePath = `M${pathStartX},${pathStartY} L${pathStartX},${pathMidY} L${pathEndX},${pathMidY}`

    const heightRatio = height / (width + height)
    const animationDuration = '10s'

    return {
      tracePath,
      animationDuration,
      heightRatio,
      indicatorSize: {
        width: strokeWidth * 2,
        height: strokeWidth,
        rx: strokeWidth / 2,
        ry: strokeWidth / 2
      },
      widthAnimation: {
        values: `${strokeWidth * 6};${strokeWidth};${strokeWidth * 6};${strokeWidth};${strokeWidth * 6}`,
        keyTimes: `0;${(heightRatio / 2).toFixed(2)};.5;${(1 - (heightRatio / 2)).toFixed(2)};1`,
        keySplines: '0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1'
      }
    }
  }, [dimensions, layout])

  const { width, height } = dimensions
  const {
    strokeWidth,
    yAxisX,
    xAxisY,
    yAxisTop,
    yAxisBottom,
    xAxisRight,
    backgroundRect
  } = layout

  return (
    <div
      ref={loadingContainerRef}
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden'
      }}
    >
      <svg width={width} height={height}>
        <rect
          {...backgroundRect}
          fill='var(--color-neutral-100)'
          className={animate && classes.skeletonAnimation}
        />

        <line
          x1={yAxisX}
          x2={yAxisX}
          y1={yAxisTop}
          y2={yAxisBottom}
          stroke='var(--color-neutral-300)'
          strokeWidth={strokeWidth}
          strokeLinecap='round'
        />

        <line
          x1={yAxisX}
          y1={xAxisY}
          x2={xAxisRight}
          y2={xAxisY}
          stroke='var(--color-neutral-300)'
          strokeWidth={strokeWidth}
          strokeLinecap='round'
        />

        {animate && (
          <>
            <path
              id='tracePath'
              d={animations.tracePath}
              fill='none'
              stroke='none'
            />

            <rect
              {...animations.indicatorSize}
              fill='var(--color-neutral-400)'
            >
              <animateMotion
                dur={animations.animationDuration}
                repeatCount='indefinite'
                rotate='auto'
                calcMode='linear'
                keyPoints='0;1;0'
                keyTimes='0;0.5;1'
              >
                <mpath href='#tracePath' />
              </animateMotion>

              <animate
                attributeName='width'
                values={animations.widthAnimation.values}
                keyTimes={animations.widthAnimation.keyTimes}
                dur={animations.animationDuration}
                repeatCount='indefinite'
                calcMode='spline'
                keySplines={animations.widthAnimation.keySplines}
              />
            </rect>
          </>
        )}
      </svg>
    </div>
  )
}

AxisChartLoadingState.propTypes = {
  animate: PropTypes.bool
}

export default AxisChartLoadingState
